/**
 * Band-probe BA — a deliberate, throwaway availability check whose ONLY purpose is to
 * provoke a 670 ("Alterseintrag ist falsch oder fehlt") and parse the hotel's child age
 * band out of the resulting hint (`"…Kind muss zwischen 4 und 12 liegen"`).
 *
 * Why a separate probe (spec §0.2 / §7): the band hint appears ONLY on a FAILED (670) BA.
 * A successful (712) BA carries no band — only payment hints. So we force every participant
 * to Salutation 'K' + BirthDate(real age): any adult (age 35) is far outside any child band,
 * which guarantees a 670 whenever adults are present. The session/activityId from this call
 * are read and DISCARDED — the probe never touches the real booking.
 *
 * Edge: an all-in-band party (e.g. a single 8yo child, band 4–12) returns 712 with no hint;
 * `parseAgeBandFromHints` then yields null and the caller falls back to FALLBACK_BANDS.
 *
 * This function NEVER throws — any error resolves to null (band unknown → fallback).
 *
 * See claude/specs/ChildVsInfant/IMPLEMENTATION-SPEC.md §7.
 */

import { performBookingAction } from './performAction';
import {
  formatDateForDaVinci,
  createDefaultClient,
  createMultiRoomServices,
  calculateBirthDate,
} from './helpers';
import { DAVINCI_CONFIG } from './config';
import type { RoomCombination } from '@/services/mappers/offerMapper';
import type { PassengerInfo } from '@/types/schemas/player/common';
import type { DaVinciParticipant } from '@/types/schemas';
import { parseAgeBandFromHints } from '@/types/schemas/checkout/ageBands';

/**
 * Build the all-`K` probe participants: every passenger as Salutation 'K' + BirthDate
 * (derived from real age, defaulting to an adult 35). Adults at 35 as 'K' fall outside any
 * child band, forcing the 670 we need.
 */
function createProbeParticipants(passengers: PassengerInfo[]): DaVinciParticipant[] {
  return passengers.map((p) => ({
    Salutation: 'K',
    Gender: 0,
    Name: 'NN/NN',
    DoNotAutoCreateClient: true,
    Age: { BirthDate: calculateBirthDate(p.age ?? 35) },
    Passport: {
      Nationality: DAVINCI_CONFIG.DEFAULT_NATIONALITY,
      NationalityCode: DAVINCI_CONFIG.DEFAULT_NATIONALITY_CODE,
    },
  }));
}

/**
 * Fire the band probe. Returns the parsed `{ childMin, childMax }` from the 670 hint, or
 * `null` when no band could be obtained (712 with no hint, parse failure, or any error).
 *
 * @param combination - The room combination (reused for Services, identical to BA #1).
 * @param productCode - Product Code (GIATA ID), as DaVinci "Service".
 */
export async function fireBandProbe(
  combination: RoomCombination,
  productCode: string
): Promise<{ childMin: number; childMax: number } | null> {
  try {
    const services = createMultiRoomServices(combination, productCode);
    const participants = createProbeParticipants(combination.allPassengers);

    const bookingRequest = {
      ActionCode: 'BA' as const,
      AgencyNumber: DAVINCI_CONFIG.AGENCY_NUMBER,
      Touroperator: DAVINCI_CONFIG.TOUROPERATOR,
      TravelType: '',
      SalesOffice: '',
      BookingDate: formatDateForDaVinci(new Date()),
      Services: services,
      Client: createDefaultClient(),
      Participants: participants,
    };

    const response = await performBookingAction(bookingRequest, {});

    // 670 is the EXPECTED path here — never surfaced as an error. Session/ActivityId ignored.
    const booking = response.Booking as Record<string, unknown>;
    const hints = booking.Hints as Array<{ Info?: string }> | undefined;

    return parseAgeBandFromHints(hints);
  } catch (error) {
    console.warn('[bandProbe] probe failed; falling back to default bands.', error);
    return null;
  }
}
